import React, { useEffect, useState } from 'react'

import {
    Alert,
    Button,
    Descriptions,
    Divider,
    Empty,
    Input,
    List,
    message,
    PageHeader,
    Popconfirm,
    Select,
    Statistic,
    Steps,
    Row,
    Col,
    Table,
    Tag,
    Upload,
} from 'antd'

import {
    CloudUploadOutlined,
    DeleteOutlined,
    FileExcelFilled,
    FileZipOutlined
} from '@ant-design/icons'

import axios from 'axios'
import Dragger from 'antd/lib/upload/Dragger'
import { useSelector } from 'react-redux'
import { useApi } from '../../Utils'
import { serverUrl } from '../../Constants'
import { LogoRouge } from '../../Assets'
import calculateMenu from './calculateMenu'

type PriceList = {
    id: string
    name: string
    productos?: number
    uploadedAt?: number
}

type SucursalRow = {
    nroSuc: string
    sucursal: string
    lineas: number
    matched: number
    viaEan: number
    sinCatalogo: number
    unmatched: Array<{ artProv: string; ean: string; descripcion: string; cantidad: number }>
}

/**
 * Inyectador "OC GRETA CONSIGNADO" (Argentina, clientId 12).
 *
 * Sigue el patrón de los inyectadores (WarehouseInjector): lista de clientes a la
 * izquierda (Rouge) y, al seleccionar, un flujo por Steps (1 OC → 2 Resumen) con la
 * tarjeta de carga tipo Excel a la izquierda y la configuración a la derecha.
 *
 * - La OC GRETA CONSIGNADO es el archivo que cambia con cada pedido (se sube).
 * - La plantilla DOC es el formato FIJO de salida (se reemplaza solo si cambia el
 *   formato). La lista de precios/catálogo viene de Bejerman PP o del xlsx maestro.
 */
export default function InjectorGretaOC({ exposeSubMenu, setMainLoading }: any) {
    const { permissions, userInfo, token } = useSelector((state: any) => state)
    const isAdmin = (permissions as string[]).includes('Administrador')

    const [active, setActive] = useState<boolean>(false)
    const [step, setStep] = useState<number>(0)
    const [ocFile, setOcFile] = useState<File | null>(null)

    // Lista de precios (catálogo de match) elegida en el selector. null = usar los
    // datos embebidos de la plantilla DOC. La lista Bejerman PP queda deshabilitada.
    const [priceListId, setPriceListId] = useState<string | null>(null)
    const [uploadName, setUploadName] = useState<string>('')
    const [deletingId, setDeletingId] = useState<string | null>(null)
    const source: 'xlsx' | 'pricelist' = priceListId ? 'pricelist' : 'xlsx'

    // Endpoints backend BOL (Bearer token).
    const master = useApi<any>('get', 'v3/ar/injector/greta/master', { onRequest: false, requiredToken: true })
    const previewApi = useApi<any>('post', 'v3/ar/injector/greta/preview', { onRequest: true, requiredToken: true })
    const generateApi = useApi<any>('post', 'v3/ar/injector/greta/generate', { onRequest: true, requiredToken: true })
    const uploadMasterApi = useApi<any>('post', 'v3/ar/injector/greta/master', { onRequest: true, requiredToken: true })
    const priceListsApi = useApi<any>('get', 'v3/ar/injector/greta/pricelists', { onRequest: true, requiredToken: true })
    const uploadPriceListApi = useApi<any>('post', 'v3/ar/injector/greta/pricelists', { onRequest: true, requiredToken: true })

    const priceLists: PriceList[] = priceListsApi.data?.listas || []

    useEffect(() => {
        if (exposeSubMenu) exposeSubMenu(calculateMenu(permissions, userInfo?.id_client))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permissions])

    useEffect(() => { setMainLoading(false) }, [setMainLoading])

    useEffect(() => { if (previewApi.errors?.length) message.error(previewApi.errors[0]?.statusMessage || 'Error al procesar la OC') }, [previewApi.errors])
    useEffect(() => { if (generateApi.errors?.length) message.error(generateApi.errors[0]?.statusMessage || 'Error al generar los archivos') }, [generateApi.errors])
    useEffect(() => { if (uploadMasterApi.errors?.length) message.error(uploadMasterApi.errors[0]?.statusMessage || 'Error al subir la plantilla') }, [uploadMasterApi.errors])
    useEffect(() => { if (priceListsApi.errors?.length) message.error(priceListsApi.errors[0]?.statusMessage || 'Error al cargar las listas de precios') }, [priceListsApi.errors])
    useEffect(() => { if (uploadPriceListApi.errors?.length) message.error(uploadPriceListApi.errors[0]?.statusMessage || 'Error al subir la lista de precios') }, [uploadPriceListApi.errors])

    // Al cambiar la lista de precios seleccionada, recalcular el preview si ya hay OC.
    useEffect(() => {
        if (ocFile) previewApi.fetch(buildFd(ocFile))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceListId])

    useEffect(() => {
        if (uploadPriceListApi.data?.status === 'success') {
            const lista = uploadPriceListApi.data?.lista
            message.success(`Lista "${lista?.name}" cargada (${lista?.productos ?? 0} productos).`)
            setUploadName('')
            priceListsApi.refetch()
            if (lista?.id) setPriceListId(lista.id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadPriceListApi.data])

    useEffect(() => {
        if (generateApi.data?.url) {
            window.open(generateApi.data.url, '_blank')
            message.success('Archivos generados. Se abrió la descarga del ZIP.')
        }
    }, [generateApi.data])

    useEffect(() => {
        if (uploadMasterApi.data?.status === 'success') {
            message.success('Plantilla de formato actualizada.')
            master.refetch()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadMasterApi.data])

    // Arma el FormData con la OC + la fuente de catálogo elegida en el selector.
    const buildFd = (file: File) => {
        const fd = new FormData()
        fd.append('file', file)
        fd.append('source', source)
        if (priceListId) fd.append('priceListId', priceListId)
        return fd
    }

    const runPreview = (file: File) => {
        setOcFile(file)
        previewApi.fetch(buildFd(file))
    }

    const runGenerate = () => {
        if (!ocFile) { message.warning('Primero suba una OC.'); return }
        generateApi.fetch(buildFd(ocFile))
    }

    const runUploadMaster = (file: File) => {
        const fd = new FormData()
        fd.append('file', file)
        uploadMasterApi.fetch(fd)
    }

    const runUploadPriceList = (file: File) => {
        if (!uploadName.trim()) {
            message.warning('Escribe un nombre para la lista antes de subirla.')
            return false
        }
        const fd = new FormData()
        fd.append('file', file)
        fd.append('name', uploadName.trim())
        uploadPriceListApi.fetch(fd)
        return false
    }

    // useApi no soporta DELETE; se llama axios directo con el mismo Bearer token.
    const runDeletePriceList = async (id: string) => {
        setDeletingId(id)
        try {
            await axios.delete(`${serverUrl}v3/ar/injector/greta/pricelists/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            message.success('Lista de precios eliminada.')
            if (priceListId === id) setPriceListId(null)
            priceListsApi.refetch()
        } catch (e) {
            message.error('No se pudo eliminar la lista de precios.')
        } finally {
            setDeletingId(null)
        }
    }

    const resetClient = () => { setActive(false); setStep(0) }

    const sucursales: SucursalRow[] = previewApi.data?.sucursales || []
    const totales = previewApi.data?.totales
    const masterMeta = master.data?.maestro
    const templateReady = !!masterMeta?.exists

    const columns = [
        { title: 'Nro Suc', dataIndex: 'nroSuc', key: 'nroSuc', width: 90 },
        { title: 'Sucursal', dataIndex: 'sucursal', key: 'sucursal' },
        { title: 'Líneas', dataIndex: 'lineas', key: 'lineas', width: 80, align: 'right' as const },
        { title: 'Match', dataIndex: 'matched', key: 'matched', width: 90, align: 'right' as const, render: (v: number) => <Tag color="green">{v}</Tag> },
        { title: 'vía EAN', dataIndex: 'viaEan', key: 'viaEan', width: 90, align: 'right' as const, render: (v: number) => (v ? <Tag color="blue">{v}</Tag> : <span>—</span>) },
        { title: 'Sin catálogo', dataIndex: 'sinCatalogo', key: 'sinCatalogo', width: 120, align: 'right' as const, render: (v: number) => (v ? <Tag color="orange">{v}</Tag> : <Tag color="green">0</Tag>) },
    ]

    // Clientes del inyectador AR (por ahora, Rouge).
    const clientes = [
        { id: 'rouge', title: 'GRETA CONSIGNADO', description: <>Rouge · Argentina</>, icon: <img src={LogoRouge} alt="Rouge" /> },
    ]

    const detalle = (
        <Row className="WarehouseInjector--Row">
            <Col span={24}>
                <Steps current={step} items={[
                    { title: 'OC', description: 'Carga y configuración' },
                    { title: 'Resumen', description: 'Validación y generación' },
                ]} />

                {step === 0 && (
                    <div className="WarehouseInjector--Row--Step">
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Dragger
                                    accept=".xlsx"
                                    maxCount={1}
                                    showUploadList={false}
                                    beforeUpload={(file) => { runPreview(file as unknown as File); return false }}
                                    disabled={!templateReady}
                                >
                                    <p className="ant-upload-drag-icon"><FileExcelFilled /></p>
                                    <p className="ant-upload-text">Selecciona la OC para cargar</p>
                                    <p className="ant-upload-hint">
                                        Carga la <strong>OC GRETA CONSIGNADO</strong> del pedido (cambia con cada pedido).
                                        Ej: OC GRETA CONSIGNADO 16.06.26.xlsx
                                    </p>
                                </Dragger>
                                {ocFile && (
                                    <p style={{ marginTop: 12 }}>
                                        <FileExcelFilled style={{ color: '#217346' }} /> <strong>{ocFile.name}</strong>
                                        {previewApi.loading && <span> · procesando…</span>}
                                        {!!totales && <span> · {totales.lineas} líneas, {sucursales.length} sucursales</span>}
                                    </p>
                                )}
                            </Col>

                            <Col xs={24} md={12}>
                                {!templateReady && (
                                    <Alert
                                        style={{ marginBottom: 16 }}
                                        type="warning"
                                        showIcon
                                        message="Falta la plantilla de formato (DOC)"
                                        description={isAdmin
                                            ? 'Súbela abajo antes de procesar la OC. Define el formato de salida.'
                                            : 'Pide a un Administrador que cargue la plantilla DOC de formato.'}
                                    />
                                )}

                                <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
                                    <Descriptions.Item label="Plantilla de formato (DOC)">
                                        {templateReady ? <Tag color="green">Cargada</Tag> : <Tag color="red">Sin cargar</Tag>}
                                        {templateReady && masterMeta?.lastModified
                                            ? <span style={{ marginLeft: 8, color: '#888' }}>{new Date(masterMeta.lastModified * 1000).toLocaleDateString('es-CL')}</span>
                                            : null}
                                    </Descriptions.Item>
                                </Descriptions>

                                {isAdmin && (
                                    <Upload
                                        accept=".xlsx"
                                        maxCount={1}
                                        showUploadList={false}
                                        beforeUpload={(file) => { runUploadMaster(file as unknown as File); return false }}
                                    >
                                        <Button icon={<CloudUploadOutlined />} loading={uploadMasterApi.loading} size="small">
                                            {templateReady ? 'Reemplazar plantilla DOC (formato fijo)' : 'Subir plantilla DOC (formato fijo)'}
                                        </Button>
                                    </Upload>
                                )}

                                <Divider style={{ margin: '20px 0 12px' }} orientation="left" plain>
                                    Lista de precios (catálogo)
                                </Divider>

                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="Plantilla (datos embebidos)"
                                    loading={priceListsApi.loading}
                                    allowClear
                                    value={priceListId || undefined}
                                    onChange={(v) => setPriceListId((v as string) || null)}
                                    options={[
                                        { value: '', label: 'Plantilla (datos embebidos)' },
                                        ...priceLists.map((pl) => ({
                                            value: pl.id,
                                            label: `${pl.name}${pl.productos ? ` · ${pl.productos} prod.` : ''}`,
                                        })),
                                    ]}
                                />
                                <p style={{ color: '#888', fontSize: 12, marginTop: 6 }}>
                                    Define los códigos/precios contra los que se hace el match. El formato de salida
                                    lo sigue definiendo la plantilla DOC.
                                </p>

                                {isAdmin && (
                                    <div style={{ marginTop: 8 }}>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <Input
                                                placeholder="Nombre de la lista (ej: Shiseido AR jul-2026)"
                                                value={uploadName}
                                                onChange={(e) => setUploadName(e.target.value)}
                                            />
                                            <Upload
                                                accept=".xlsx"
                                                maxCount={1}
                                                showUploadList={false}
                                                beforeUpload={(file) => runUploadPriceList(file as unknown as File)}
                                            >
                                                <Button icon={<CloudUploadOutlined />} loading={uploadPriceListApi.loading}>
                                                    Subir lista
                                                </Button>
                                            </Upload>
                                        </div>

                                        {priceLists.length > 0 && (
                                            <List
                                                size="small"
                                                style={{ marginTop: 8 }}
                                                dataSource={priceLists}
                                                renderItem={(pl) => (
                                                    <List.Item
                                                        actions={[
                                                            <Popconfirm
                                                                title="¿Eliminar esta lista de precios?"
                                                                okText="Eliminar"
                                                                cancelText="Cancelar"
                                                                onConfirm={() => runDeletePriceList(pl.id)}
                                                            >
                                                                <Button
                                                                    type="text"
                                                                    danger
                                                                    size="small"
                                                                    icon={<DeleteOutlined />}
                                                                    loading={deletingId === pl.id}
                                                                />
                                                            </Popconfirm>,
                                                        ]}
                                                    >
                                                        <List.Item.Meta
                                                            title={pl.name}
                                                            description={`${pl.productos ?? 0} productos${pl.uploadedAt ? ` · ${new Date(pl.uploadedAt * 1000).toLocaleDateString('es-CL')}` : ''}`}
                                                        />
                                                    </List.Item>
                                                )}
                                            />
                                        )}
                                    </div>
                                )}

                                <div style={{ textAlign: 'right', marginTop: 32 }}>
                                    <Button
                                        type="primary"
                                        size="large"
                                        disabled={!templateReady || !sucursales.length || previewApi.loading}
                                        onClick={() => setStep(1)}
                                    >
                                        Continuar
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>
                )}

                {step === 1 && (
                    <div className="WarehouseInjector--Row--Step">
                        <Row gutter={16} style={{ marginBottom: 8, height: 'auto', flex: 'none' }}>
                            <Col span={6}><Statistic title="Líneas OC" value={totales?.lineas ?? 0} /></Col>
                            <Col span={6}><Statistic title="Con match" value={totales?.matched ?? 0} valueStyle={{ color: '#3f8600' }} /></Col>
                            <Col span={6}><Statistic title="vía EAN" value={totales?.viaEan ?? 0} valueStyle={{ color: '#1677ff' }} /></Col>
                            <Col span={6}><Statistic title="Sin catálogo" value={totales?.sinCatalogo ?? 0} valueStyle={{ color: '#d46b08' }} /></Col>
                        </Row>

                        <Table
                            rowKey="nroSuc"
                            size="small"
                            loading={previewApi.loading}
                            dataSource={sucursales}
                            columns={columns}
                            pagination={false}
                            expandable={{
                                rowExpandable: (r) => r.sinCatalogo > 0,
                                expandedRowRender: (r) => (
                                    <Table
                                        rowKey={(u: any) => `${r.nroSuc}-${u.artProv}-${u.ean}`}
                                        size="small"
                                        pagination={false}
                                        dataSource={r.unmatched}
                                        columns={[
                                            { title: 'Art. Proveedor', dataIndex: 'artProv', key: 'artProv', width: 140 },
                                            { title: 'Descripción', dataIndex: 'descripcion', key: 'descripcion' },
                                            { title: 'EAN', dataIndex: 'ean', key: 'ean', width: 160 },
                                            { title: 'Cantidad', dataIndex: 'cantidad', key: 'cantidad', width: 100, align: 'right' as const },
                                        ]}
                                    />
                                ),
                            }}
                        />

                        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
                            <Button onClick={() => setStep(0)}>Atrás</Button>
                            <span>
                                {generateApi.data?.url && (
                                    <Button type="link" icon={<CloudUploadOutlined />} href={generateApi.data.url} target="_blank">
                                        Volver a descargar
                                    </Button>
                                )}
                                <Button type="primary" size="large" icon={<FileZipOutlined />} loading={generateApi.loading} onClick={runGenerate}>
                                    Generar archivos por sucursal (ZIP)
                                </Button>
                            </span>
                        </div>
                    </div>
                )}
            </Col>
        </Row>
    )

    const back = !active ? {} : { onBack: resetClient }

    return (
        <div className="App">
            <div className="WarehouseInjector">
                <PageHeader
                    className="site-page-header"
                    {...back}
                    title={!active ? 'Inyectador OC' : 'GRETA CONSIGNADO (Rouge)'}
                    subTitle={!active
                        ? 'Selecciona un cliente para cargar su OC'
                        : 'Haz click aquí para volver al listado de clientes'}
                />

                <div className="Wrapper">
                    <Row>
                        {!active && (
                            <Col xs={24} md={7}>
                                <List
                                    itemLayout="horizontal"
                                    dataSource={clientes}
                                    renderItem={(item) => (
                                        <List.Item className="hovered" onClick={() => setActive(true)}>
                                            <List.Item.Meta avatar={item.icon} title={item.title} description={item.description} />
                                        </List.Item>
                                    )}
                                />
                            </Col>
                        )}
                        <Col xs={24} md={!active ? 17 : 24} className="WarehouseInjector--ReportFrame">
                            {active
                                ? detalle
                                : <Empty description={<>Selecciona <strong>Rouge</strong> en el menú izquierdo para cargar la OC.</>} />}
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    )
}
